import { yupResolver } from "@hookform/resolvers/yup";
import { Form } from "apps/visits/packages/ui/src/components";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FormProvider, type SubmitErrorHandler, type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { PhoneCheckoutStep, UserData } from "../../hooks/use-phone-checkout-flow";
import { useCartState } from "../../store/use-cart-state";
import { finishCheckoutErrorEvent, safeTrackEvent, signUpErrorEvent } from "../../tracking/events";
import type { ConfigName } from "../../types/config-name";
import { type FormValues, useValidationSchema } from "../user-data-form/validation-schema";
import { CardCheckoutLayout, type CardsValidationHandle } from "./card-checkout-layout";
import { CustomerSection } from "./patient-form-parts/customer-section";
import { PatientSection } from "./patient-form-parts/patient-section";

interface PatientFormProps {
  configName: ConfigName;
  // Customer-section description, used by the standard (non-F&F) variants
  // where it replaces the page-level paragraph that used to sit under the
  // back button. F&F keeps its own dynamic cmsTranslations copy.
  description?: string;
  apiError?: string;
  apiErrorCode?: string;
  onSubmit: SubmitHandler<FormValues>;
  submitRef: React.RefObject<HTMLButtonElement>;
  validateRef?: React.MutableRefObject<(() => Promise<boolean>) | null>;
  bearerRegistrationRequired?: boolean;
  patientPhoneRequired?: boolean;
  initialValues?: Partial<FormValues>;
  variant?: "friends-and-family" | "standard";
  hasLabplus?: boolean;
  checkoutBlockedEmailDomains?: string[];
  phoneCheckoutStep?: PhoneCheckoutStep;
  prefilledUserData?: UserData | null;
  showPasswordFields?: boolean;
  readonlyCustomerFields?: boolean;
  phoneAuthContent?: React.ReactNode;
  currentPhone?: { dialCode: string; phone: string } | null;
  simplifiedRegistrationAvailable?: boolean;
  isGuestCheckout?: boolean;
  onGuestCheckoutChange?: (isGuest: boolean) => void;
  activeCard?: "patient" | "customer" | null;
  onActiveCardChange?: (card: "patient" | "customer" | null) => void;
  lockCustomerEdit?: boolean;
  cardsValidationRef?: React.MutableRefObject<CardsValidationHandle | null>;
}

export var PatientForm = ({
  configName,
  description,
  apiError,
  apiErrorCode,
  onSubmit,
  bearerRegistrationRequired = true,
  patientPhoneRequired = false,
  hasLabplus = false,
  submitRef,
  validateRef,
  initialValues,
  variant = "friends-and-family",
  checkoutBlockedEmailDomains = [],
  phoneCheckoutStep,
  prefilledUserData,
  showPasswordFields = false,
  readonlyCustomerFields = false,
  phoneAuthContent,
  currentPhone,
  simplifiedRegistrationAvailable = false,
  isGuestCheckout = false,
  onGuestCheckoutChange,
  activeCard,
  onActiveCardChange,
  lockCustomerEdit,
  cardsValidationRef,
}: PatientFormProps) => {
  const { t } = useTranslation();

  const mergedInitialValues = React.useMemo(
    () => ({
      // Default both dial codes to Poland — a missing default lets RHF read the registered
      // <select>'s DOM value before its `defaultValue` prop applies, capturing the first
      // option (+93 Afghanistan) on first render.
      dialCode: "+48",
      patientDialCode: "+48",
      acceptDataProcessing: false,
      acceptTouNPp: false,
      acceptNewsletter: false,
      acceptMarketingPhoneContact: false,
      isGuestCheckout: false,
      ...initialValues,
      orderType: initialValues?.orderType || "for_self",
      ...(prefilledUserData && {
        firstName: prefilledUserData.firstName,
        lastName: prefilledUserData.lastName,
        email: prefilledUserData.email,
        // Unified marketing checkbox renders unchecked unless backend reports
        // BOTH consents already granted (then it's hidden entirely). Carrying
        // over a single-true mixed state would visually pre-check the merged
        // checkbox without the user opting in to the other consent.
        ...(() => {
          const allPrefilled = isGuestCheckout
            ? !!prefilledUserData.acceptMarketingPhoneContact
            : !!(prefilledUserData.acceptNewsletter && prefilledUserData.acceptMarketingPhoneContact);
          return {
            acceptNewsletter: allPrefilled ? prefilledUserData.acceptNewsletter : false,
            acceptMarketingPhoneContact: allPrefilled ? prefilledUserData.acceptMarketingPhoneContact : false,
          };
        })(),
      }),
    }),
    [initialValues, prefilledUserData, isGuestCheckout],
  );

  const methods = useForm<FormValues>({
    mode: "onTouched",
    defaultValues: mergedInitialValues,
    resolver: yupResolver(
      useValidationSchema(t, {
        bearerRequired: bearerRegistrationRequired,
        patientPhoneRequired,
        showPasswordFields,
        readonlyCustomerFields,
        hidePhone: !!phoneCheckoutStep,
        checkoutBlockedEmailDomains,
        isGuestCheckout,
        // Friends & Family is the one distribution where the account holder may order for a child.
        allowMinorPatientForSomeone: configName === "friends_and_family",
      }),
    ),
  });
  const { handleSubmit, errors, watch, setValue, setError, reset, getValues, trigger } = methods;
  const values = watch();
  const noPesel = values.noPesel;
  const orderType = values.orderType;

  useEffect(() => {
    if (!validateRef) return;
    validateRef.current = () => trigger();
    return () => {
      validateRef.current = null;
    };
  }, [validateRef, trigger]);

  const hasCoreData =
    values.orderType === "for_someone"
      ? !!values.patientFirstName ||
        !!values.patientLastName ||
        !!values.pesel ||
        !!values.dateOfBirth ||
        !!values.gender ||
        !!values.patientPhone
      : !!values.pesel || !!values.dateOfBirth || !!values.gender;

  const coreRequired = bearerRegistrationRequired || hasCoreData;

  type PatientFields = { pesel?: string; noPesel?: boolean; dateOfBirth?: string; gender?: string };
  const patientFieldsCache = useRef<Record<string, PatientFields>>({
    for_self: {},
    for_someone: {},
  });

  const handleOrderTypeChange = useCallback(
    (newOrderType: string, formOnChange: (val: string) => void) => {
      const currentOrderType = values.orderType;
      if (newOrderType === currentOrderType) return;

      patientFieldsCache.current[currentOrderType] = {
        pesel: values.pesel,
        noPesel: values.noPesel,
        dateOfBirth: values.dateOfBirth,
        gender: values.gender,
      };

      const cached = patientFieldsCache.current[newOrderType];
      setValue("pesel", cached.pesel ?? "");
      setValue("noPesel", cached.noPesel ?? false);
      setValue("dateOfBirth", cached.dateOfBirth ?? "");
      setValue("gender", cached.gender ?? "");

      formOnChange(newOrderType);
    },
    [values.orderType, values.pesel, values.noPesel, values.dateOfBirth, values.gender, setValue],
  );

  useEffect(() => {
    setValue("isGuestCheckout", isGuestCheckout);
  }, [isGuestCheckout, setValue]);

  const showGuestOption =
    simplifiedRegistrationAvailable && (phoneCheckoutStep === "NewUser" || phoneCheckoutStep === "NewUserOTPOverlay");

  const savedNewsletterConsent = useRef(false);

  const handleGuestCheckoutToggle = useCallback(
    (guest: boolean) => {
      onGuestCheckoutChange?.(guest);
      setValue("isGuestCheckout", guest);
      if (guest) {
        savedNewsletterConsent.current = getValues("acceptNewsletter");
        setValue("email", "");
        setValue("emailConfirmation", "");
        setValue("password", "");
        setValue("passwordConfirmation", "");
        setValue("acceptNewsletter", false);
      } else {
        setValue("acceptNewsletter", savedNewsletterConsent.current);
      }
    },
    [onGuestCheckoutChange, setValue, getValues],
  );

  const consentFieldsForSelectAll = isGuestCheckout
    ? (["acceptDataProcessing", "acceptTouNPp", "acceptMarketingPhoneContact"] as const)
    : (["acceptDataProcessing", "acceptTouNPp", "acceptNewsletter", "acceptMarketingPhoneContact"] as const);

  const allMarketingPrefilled = isGuestCheckout
    ? !!prefilledUserData?.acceptMarketingPhoneContact
    : !!(prefilledUserData?.acceptNewsletter && prefilledUserData?.acceptMarketingPhoneContact);
  const showMarketingConsent = !readonlyCustomerFields || !allMarketingPrefilled;

  // Select-all is derived from the two master fields only — they are
  // controlled by Controllers, so watch() on them is reliably reactive in
  // RHF v6. Mirror fields are kept in sync via setValue() in onChange
  // handlers; we don't need to subscribe to them here.
  const isSelectAllChecked = !!watch("acceptTouNPp") && !!watch("acceptMarketingPhoneContact");

  const { products } = useCartState();

  const privacyPolicyHref = "/regulamin";
  const termsHref = "/regulamin";

  useEffect(() => {
    if (!prefilledUserData) return;
    const syncValues = () => {
      const current = getValues();
      // See mergedInitialValues comment — mixed state must render unchecked.
      const allMarketingPrefilledNow = isGuestCheckout
        ? !!prefilledUserData.acceptMarketingPhoneContact
        : !!(prefilledUserData.acceptNewsletter && prefilledUserData.acceptMarketingPhoneContact);
      reset({
        ...current,
        firstName: prefilledUserData.firstName,
        lastName: prefilledUserData.lastName,
        email: prefilledUserData.email,
        acceptNewsletter: allMarketingPrefilledNow ? prefilledUserData.acceptNewsletter : false,
        acceptMarketingPhoneContact: allMarketingPrefilledNow ? prefilledUserData.acceptMarketingPhoneContact : false,
        // Preserve patient data fields that might have been filled before phone auth
        orderType: current.orderType,
        patientFirstName: current.patientFirstName,
        patientLastName: current.patientLastName,
        pesel: current.pesel,
        noPesel: current.noPesel,
        dateOfBirth: current.dateOfBirth,
        gender: current.gender,
        patientPhone: current.patientPhone,
        patientDialCode: current.patientDialCode,
      });
    };
    // Delay to ensure fields are mounted after conditional render
    const timeout = setTimeout(syncValues, 50);
    return () => clearTimeout(timeout);
  }, [prefilledUserData, reset, getValues, isGuestCheckout]);

  // Key to signal CardCheckoutLayout to reset the customer snapshot
  const [customerResetKey, setCustomerResetKey] = useState(0);
  const prevPhoneCheckoutStepRef = useRef(phoneCheckoutStep);
  useEffect(() => {
    const prev = prevPhoneCheckoutStepRef.current;
    prevPhoneCheckoutStepRef.current = phoneCheckoutStep;
    // Detect "Zmień numer" – step returned to PhoneInput from another state
    if (prev && prev !== "PhoneInput" && phoneCheckoutStep === "PhoneInput") {
      const current = getValues();
      reset({
        ...current,
        firstName: "",
        lastName: "",
        email: "",
        emailConfirmation: "",
        password: "",
        passwordConfirmation: "",
        isGuestCheckout: false,
        acceptDataProcessing: false,
        acceptTouNPp: false,
        acceptNewsletter: false,
        acceptMarketingPhoneContact: false,
      });
      onGuestCheckoutChange?.(false);
      setCustomerResetKey((k) => k + 1);
    }
  }, [phoneCheckoutStep, reset, getValues, onGuestCheckoutChange]);

  useEffect(() => {
    if (apiError || Object.keys(errors).length > 0) {
      // Order: explicit backend code > raw API message > first invalid field > "validation".
      // Falling straight from apiErrorCode to the joined keys list lost backend errors that
      // arrived without a code, and joining all keys produced unbounded cardinality in GA4.
      const errorType = apiErrorCode || apiError || Object.keys(errors).sort()[0] || "validation";
      safeTrackEvent(
        () =>
          finishCheckoutErrorEvent(
            products.map((p) => ({ ...p, productType: "blood-test-package" as const, quantity: 1 })),
            {
              value: products.reduce((acc, p) => acc + Number(p.grossPrice), 0),
            },
            { errorType, numberOfProducts: products.length },
          ),
        "finish_checkout_error",
        "patient-form api/validation error",
      );
    }
  }, [apiError, apiErrorCode, errors, products]);

  useEffect(() => {
    if (!apiErrorCode) return;
    if (apiErrorCode.toLowerCase() === "bearer.pesel_invalid") {
      const field = noPesel ? "dateOfBirth" : "pesel";
      setError(field, { type: "api", message: t("VOUCHERS_DISTRIBUTION.ERRORS.BEARER.PESEL_INVALID") });
    }
  }, [apiErrorCode, noPesel, setError, t]);

  const handleCancelPatient = useCallback(
    (patientSnapshot: Partial<FormValues> | null) => {
      const snapshot = patientSnapshot || mergedInitialValues;
      const fields: (keyof FormValues)[] = [
        "orderType",
        "pesel",
        "noPesel",
        "dateOfBirth",
        "gender",
        "patientFirstName",
        "patientLastName",
        "patientPhone",
        "patientDialCode",
      ];
      fields.forEach((field) => {
        setValue(field, snapshot[field]);
      });
    },
    [setValue, mergedInitialValues],
  );

  const handleCancelCustomer = useCallback(
    (customerSnapshot: Partial<FormValues> | null) => {
      const snapshot = customerSnapshot || mergedInitialValues;
      const fields: (keyof FormValues)[] = [
        "firstName",
        "lastName",
        "email",
        "emailConfirmation",
        "dialCode",
        "phone",
        "acceptDataProcessing",
        "acceptTouNPp",
        "acceptNewsletter",
        "acceptMarketingPhoneContact",
        "isGuestCheckout",
      ];
      fields.forEach((field) => {
        setValue(field, snapshot[field]);
      });
      if (snapshot.isGuestCheckout !== undefined) {
        onGuestCheckoutChange?.(snapshot.isGuestCheckout);
      }
    },
    [setValue, mergedInitialValues, onGuestCheckoutChange],
  );

  // Mirrors GAO `handleRegistrationInvalidSubmit`: only fire `sign_up_error` on actual submit
  // attempts in the standard new-user path. Guest checkout maps to a `verification_*` flow,
  // and existing-user/data-loaded paths aren't a sign-up. Bounded cardinality: first invalid
  // field name (alphabetically) so GA4 dimensions don't blow up.
  const handleInvalidSubmit: SubmitErrorHandler<FormValues> = (formErrors) => {
    if (phoneCheckoutStep !== "NewUser" || isGuestCheckout) return;
    const errorType = Object.keys(formErrors).sort()[0] ?? "validation";
    safeTrackEvent(() => signUpErrorEvent({ errorType }), "sign_up_error", "patient-form invalid submit");
  };

  const customerSection = (
    <CustomerSection
      variant={variant}
      configName={configName}
      description={description}
      phoneAuthContent={phoneAuthContent}
      phoneCheckoutStep={phoneCheckoutStep}
      showGuestOption={showGuestOption}
      isGuestCheckout={isGuestCheckout}
      onGuestCheckoutToggle={handleGuestCheckoutToggle}
      readonlyCustomerFields={readonlyCustomerFields}
      prefilledUserData={prefilledUserData}
      showPasswordFields={showPasswordFields}
      values={values}
      coreRequired={coreRequired}
      noPesel={noPesel}
      orderType={orderType}
      showMarketingConsent={showMarketingConsent}
      isSelectAllChecked={isSelectAllChecked}
      consentFieldsForSelectAll={consentFieldsForSelectAll}
      privacyPolicyHref={privacyPolicyHref}
      termsHref={termsHref}
    />
  );

  return (
    <FormProvider {...methods}>
      <Form onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)} className="flex w-full flex-col gap-5">
        <button type="submit" className="hidden" ref={submitRef} />

        {variant === "friends-and-family" ? (
          <CardCheckoutLayout
            configName={configName}
            patientSection={
              <PatientSection
                coreRequired={coreRequired}
                orderType={orderType}
                noPesel={noPesel}
                onOrderTypeChange={handleOrderTypeChange}
                hasLabplus={hasLabplus}
                patientPhoneRequired={patientPhoneRequired}
              />
            }
            customerSection={customerSection}
            formValues={values}
            hasLabplus={hasLabplus}
            phoneCheckoutStep={phoneCheckoutStep}
            currentPhone={currentPhone}
            trigger={trigger}
            bearerRegistrationRequired={bearerRegistrationRequired}
            customerResetKey={customerResetKey}
            prefilledUserData={prefilledUserData}
            isGuestCheckout={isGuestCheckout}
            showPasswordFields={showPasswordFields}
            onCancelPatient={handleCancelPatient}
            onCancelCustomer={handleCancelCustomer}
            activeCard={activeCard}
            onActiveCardChange={onActiveCardChange}
            lockCustomerEdit={lockCustomerEdit}
            cardsValidationRef={cardsValidationRef}
            onSelectOrderType={(type) => setValue("orderType", type, { shouldValidate: true })}
          />
        ) : (
          customerSection
        )}
      </Form>
    </FormProvider>
  );
};
